/**
 * Who drove when, from the chart data: the car's stints between stops, each with the driver the
 * pit stops' driver changes name (inDriver on the in-lap, outDriver after it). The same rules as
 * the server's dashboard/my-driver.ts. Sources without driver changes (Speedhive) name nobody.
 */
import type { CarDriveTime } from "@shared/types";
import { expandDriverName, sameDriver } from "./api";

interface LapRow {
  l: number;
  ltSec: number;
  flag?: string;
  pit: number;
}
interface PitRow {
  l: number;
  inDriver?: string;
  outDriver?: string;
}

export interface Stint {
  start: number;
  end: number;
  driver: string | null;
}

export function stintsOf(laps: LapRow[], pits: PitRow[] | undefined, soleDriver: string | null, carDrivers: string[] = [], current: string | null = null): Stint[] {
  const last = laps.length ? Math.max(...laps.map((l) => l.l)) : 0;
  if (last === 0) return [];
  const stops = [...new Map((pits ?? []).filter((p) => p.l > 0 && p.l < last).map((p) => [p.l, p])).values()].sort((a, b) => a.l - b.l);
  const out: Stint[] = [];
  let start = 1;
  let driver: string | null = null;
  for (const p of stops) {
    out.push({ start, end: p.l, driver: p.inDriver?.trim() || driver || null });
    start = p.l + 1;
    driver = p.outDriver?.trim() || null;
  }
  // The stint under way: its driver from the stop that ends it, or (a replay) who is driving now.
  out.push({ start, end: last, driver: driver ?? (current?.trim() || null) });
  // SRO's pit records name drivers by surname ("Boehm"): use the car's full name when one fits.
  const named = out.map((s) => ({ ...s, driver: s.driver ? expandDriverName(s.driver, carDrivers) : null }));
  return soleDriver ? named.map((s) => ({ ...s, driver: s.driver ?? soleDriver })) : named;
}

const green = (l: LapRow) => !l.flag || ["GF", "GREEN", "G"].includes(l.flag.toUpperCase());

/** "track": the race analysis package's drive time (out of the pit lane); "laps": the driver's lap times summed. */
export type DriveSource = "track" | "laps";

export interface DriverDrive {
  stints: number;
  driveTimeS: number;
  driveSource: DriveSource;
  best: { sec: number; lap: number } | null;
}

/**
 * The package's drive time for a driver as the timing names them ("Boehm", "K. Boehm"): the one
 * entry whose name is theirs, made whole from the entry's names first (the server's my-driver.ts).
 */
export function driveTimeFor(driveTimes: CarDriveTime[] | undefined, name: string): CarDriveTime | null {
  const named = (driveTimes ?? []).filter((d): d is CarDriveTime & { name: string } => !!d.name);
  if (!named.length) return null;
  const full = expandDriverName(name, named.map((d) => d.name));
  const hits = named.filter((d) => sameDriver(d.name, full));
  return hits.length === 1 ? hits[0] : null;
}

/**
 * One driver's share of the car's race; null when the timing doesn't name who drove. Its drive
 * time is the package's when the car has one for the driver, else the laps summed.
 */
export function driveOf(laps: LapRow[], stints: Stint[], name: string, driveTimes?: CarDriveTime[]): DriverDrive | null {
  if (!stints.some((s) => s.driver)) return null;
  const mine = stints.filter((s) => s.driver && sameDriver(s.driver, name));
  const myLaps = laps.filter((l) => mine.some((s) => l.l >= s.start && l.l <= s.end));
  let best: DriverDrive["best"] = null;
  for (const l of myLaps) {
    if (l.pit === 1 || !green(l) || !(l.ltSec > 0)) continue;
    if (!best || l.ltSec < best.sec) best = { sec: l.ltSec, lap: l.l };
  }
  const track = driveTimeFor(driveTimes, name);
  return {
    stints: mine.length,
    driveTimeS: Math.round(track ? track.driveTimeS : myLaps.reduce((t, l) => t + (l.ltSec > 0 ? l.ltSec : 0), 0)),
    driveSource: track ? "track" : "laps",
    best,
  };
}

/** "3:42 h" for a drive of hours, "48 min" for less. */
export function driveTimeText(s: number): string {
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}:${String(m).padStart(2, "0")} h`;
  }
  return `${Math.max(1, Math.round(s / 60))} min`;
}

/** "≈ 3:42 h" for drive time summed from lap times (an estimate); the time alone otherwise. */
export function driveText(s: number, source?: DriveSource | null): string {
  return `${source === "laps" ? "≈ " : ""}${driveTimeText(s)}`;
}

/** What an "≈" before a drive time means. */
export const DRIVE_FROM_LAPS = "≈ from lap times: the driver's laps added up, pit laps included. A race with an analysis package gives time on track instead, out of the pit lane, as the drive-time rules count it.";

/** Every stint is the driver's own (or unnamed): a single-driver car, as in a sprint series. */
export function isSoloDrive(drive: { stints: { mine: boolean; driver: string | null }[] }): boolean {
  return drive.stints.every((s) => s.mine || !s.driver);
}
